import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
  debounceTime
} from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as LabResultsActions from './lab-results.actions';
import * as LabResultsSelectors from './lab-results.selectors';
import { APIService } from '../../services/api/api.service';
import { of } from 'rxjs';

@Injectable()
export class LabResultsEffects {
  loadLabResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.loadLabResults),
      switchMap(action =>
        this.apiService
          .getLabResultsByCustomer(action.filters, action.customerId)
          .pipe(
            map(labResults =>
              LabResultsActions.loadLabResultsSuccess({
                labResults: labResults || []
              })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error(
                'Error',
                'There was an error loading lab results.'
              );
              return of(LabResultsActions.loadLabResultsFailure({ error }));
            })
          )
      )
    )
  );

  loadAllLabResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.loadAllLabResults),
      withLatestFrom(this.store.select(LabResultsSelectors.getLimitOffset)),
      debounceTime(500),
      switchMap(([{ filters, loadType }, { limit, offset }]) => {
        const newOffset =
          loadType === 'older'
            ? offset + limit
            : loadType === 'new'
            ? 0
            : offset;
        return this.apiService
          .getLabResults({ ...filters, limit, offset: newOffset })
          .pipe(
            map(labResults =>
              LabResultsActions.loadAllLabResultsSuccess({
                labResults,
                loadType: loadType || 'new',
                limit,
                offset: newOffset
              })
            ),
            catchError(error => {
              console.error('Error', error);
              return of(LabResultsActions.loadAllLabResultsFailure({ error }));
            })
          );
      })
    )
  );

  updateLabResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.updateLabResult),
      switchMap(action =>
        this.apiService.updateLabResult(action.id, action.labResult).pipe(
          tap(() => this.notification.success('Successfully updated.', '')),
          map(labResult =>
            LabResultsActions.updateLabResultSuccess({ labResult })
          ),

          catchError(error => {
            console.error('Error', error);
            this.notification.error(
              'Error',
              'There was an error updating lab result.'
            );
            return of(LabResultsActions.updateLabResultFailure({ error }));
          })
        )
      )
    )
  );

  openLabResultPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.openLabResultPdf),
      switchMap(action =>
        this.apiService.getLabResultPdfSignedUrl(action.labResultId).pipe(
          map(response => {
            if (response.success && response.signedUrl) {
              return LabResultsActions.openLabResultPdfSuccess({
                signedUrl: response.signedUrl
              });
            } else {
              this.notification.error('Error', 'Failed to generate PDF URL');
              return LabResultsActions.openLabResultPdfFailure({
                error: 'Failed to generate PDF URL'
              });
            }
          }),
          catchError(error => {
            console.error('Error getting signed URL:', error);
            this.notification.error(
              'Error',
              'Failed to open PDF. Please try again.'
            );
            return of(
              LabResultsActions.openLabResultPdfFailure({
                error: error.message || 'Unknown error'
              })
            );
          })
        )
      )
    )
  );

  openLabResultPdfSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LabResultsActions.openLabResultPdfSuccess),
        tap(action => {
          window.open(action.signedUrl, '_blank');
        })
      ),
    { dispatch: false }
  );

  createLabResult$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.createLabResult),
      switchMap(action =>
        this.apiService
          .createLabResult(action.customerId, action.labResult, action.file)
          .pipe(
            tap(() =>
              this.notification.success(
                'Success',
                'Lab result created successfully.'
              )
            ),
            map(labResult =>
              LabResultsActions.createLabResultSuccess({ labResult })
            ),
            catchError(error => {
              console.error('Error creating lab result:', error);
              this.notification.error(
                'Error',
                error?.error?.reason ||
                  error?.message ||
                  'Failed to create lab result. Please try again.'
              );
              return of(
                LabResultsActions.createLabResultFailure({
                  error: error.message || 'Unknown error'
                })
              );
            })
          )
      )
    )
  );

  submitLabResultPdf$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LabResultsActions.submitLabResultPdf),
      switchMap(action =>
        this.apiService.submitLabResultPdf(action.customerId, action.file).pipe(
          tap(() =>
            this.notification.success(
              'Success',
              'PDF submitted. AI processing will continue in the background.'
            )
          ),
          map(response =>
            LabResultsActions.submitLabResultPdfSuccess({
              response,
              customerId: action.customerId
            })
          ),
          catchError(error => {
            console.error('Error submitting PDF for AI processing:', error);
            this.notification.error(
              'Error',
              error?.error?.reason ||
                error?.message ||
                'Failed to submit PDF for AI processing. Please try again.'
            );
            return of(
              LabResultsActions.submitLabResultPdfFailure({
                error: error.message || 'Unknown error'
              })
            );
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private apiService: APIService,
    private notification: NzNotificationService,
    private store: Store
  ) {}
}
